import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { LayoutDashboard, Megaphone, HandCoins, Users, Mail, LogOut, Plus, Pencil, Trash2, Check, X, FileText, Download, Send, Newspaper, Upload, ArrowUpDown, RefreshCw, Eye, EyeOff, Globe, Ban, UserPlus } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";

const EMPTY_CAMPAIGN = {
  title_en: "", title_fr: "", title_de: "", title_tr: "", category: "youth",
  summary_en: "", summary_fr: "", summary_de: "", summary_tr: "",
  description_en: "", description_fr: "", description_de: "", description_tr: "",
  purpose_en: "", purpose_fr: "", purpose_de: "", purpose_tr: "",
  goal_amount: 1000, raised_amount: 0, image_url: "", status: "active", slug: "",
};

const EMPTY_UPDATE = { title_en: "", title_fr: "", title_de: "", title_tr: "", body_en: "", body_fr: "", body_de: "", body_tr: "", image_url: "" };

const fmt = (n) => new Intl.NumberFormat("en-BE", { style: "currency", currency: "EUR" }).format(n || 0);
const fmtDate = (d) => (d ? new Date(d).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }) : "—");

const Admin = () => {
  const navigate = useNavigate();
  const [me, setMe] = useState(null);
  const [tab, setTab] = useState("overview");
  const [stats, setStats] = useState(null);
  const [campaigns, setCampaigns] = useState([]);
  const [donations, setDonations] = useState([]);
  const [members, setMembers] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_CAMPAIGN);
  const [updOpen, setUpdOpen] = useState(false);
  const [updCampaign, setUpdCampaign] = useState(null);
  const [updates, setUpdates] = useState([]);
  const [updForm, setUpdForm] = useState(EMPTY_UPDATE);
  const [certYear, setCertYear] = useState(new Date().getFullYear());
  const [certRows, setCertRows] = useState(null);
  const [uploading, setUploading] = useState(false);
  const campaignFileRef = useRef(null);
  const updateFileRef = useRef(null);
  const [nlData, setNlData] = useState({ subject_en: "", subject_fr: "", body_en: "", body_fr: "" });
  const [nlSubs, setNlSubs] = useState(null);
  const [nlHistory, setNlHistory] = useState([]);
  const [nlSending, setNlSending] = useState(false);
  const [memberSort, setMemberSort] = useState({ key: "created_at", dir: "desc" });
  const [visitors, setVisitors] = useState(null);
  const [memberOpen, setMemberOpen] = useState(false);
  const [memberForm, setMemberForm] = useState({ name: "", email: "", plan: "yearly", amount: 100, start_date: new Date().toISOString().slice(0, 10) });

  useEffect(() => {
    api.get("/auth/me").then((r) => setMe(r.data)).catch(() => {
      localStorage.removeItem("fg_token");
      navigate("/admin/login");
    });
  }, [navigate]);

  const load = useCallback(() => {
    api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {});
    api.get("/campaigns").then((r) => setCampaigns(r.data)).catch(() => {});
    api.get("/admin/donations").then((r) => setDonations(r.data)).catch(() => {});
    api.get("/admin/members").then((r) => setMembers(r.data)).catch(() => {});
    api.get("/admin/contacts").then((r) => setContacts(r.data)).catch(() => {});
    api.get("/admin/suggestions").then((r) => setSuggestions(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (me) load();
  }, [me, load]);

  const logout = async () => {
    await api.post("/auth/logout").catch(() => {});
    localStorage.removeItem("fg_token");
    navigate("/admin/login");
  };

  const openCreate = () => {
    setEditing(null);
    setForm(EMPTY_CAMPAIGN);
    setDialogOpen(true);
  };

  const openEdit = (c) => {
    setEditing(c.id);
    setForm({
      ...EMPTY_CAMPAIGN,
      ...c,
      purpose_en: (c.purpose_en || []).join("\n"),
      purpose_fr: (c.purpose_fr || []).join("\n"),
      purpose_de: (c.purpose_de || []).join("\n"),
      purpose_tr: (c.purpose_tr || []).join("\n"),
    });
    setDialogOpen(true);
  };

  const saveCampaign = async (e) => {
    e.preventDefault();
    const payload = {
      ...form,
      goal_amount: Number(form.goal_amount) || 0,
      raised_amount: Number(form.raised_amount) || 0,
      slug: form.slug || "",
      purpose_en: form.purpose_en.split("\n").map((s) => s.trim()).filter(Boolean),
      purpose_fr: form.purpose_fr.split("\n").map((s) => s.trim()).filter(Boolean),
      purpose_de: (form.purpose_de || "").split("\n").map((s) => s.trim()).filter(Boolean),
      purpose_tr: (form.purpose_tr || "").split("\n").map((s) => s.trim()).filter(Boolean),
    };
    try {
      if (editing) await api.put(`/admin/campaigns/${editing}`, payload);
      else await api.post("/admin/campaigns", payload);
      toast.success(editing ? "Campaign updated" : "Campaign created");
      setDialogOpen(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error");
    }
  };

  const deleteCampaign = async (id) => {
    if (!window.confirm("Delete this campaign?")) return;
    try {
      await api.delete(`/admin/campaigns/${id}`);
      toast.success("Campaign deleted");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error");
    }
  };

  const confirmDonation = async (id) => {
    try {
      await api.post(`/admin/donations/${id}/confirm`);
      toast.success("Pledge confirmed");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error");
    }
  };

  const activateMember = async (id) => {
    try {
      await api.post(`/admin/members/${id}/activate`);
      toast.success("Member activated");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error");
    }
  };

  const loadUpdates = async (cid) => {
    const r = await api.get(`/campaigns/${cid}/updates`).catch(() => ({ data: [] }));
    setUpdates(r.data);
  };

  const openUpdates = (c) => {
    setUpdCampaign(c);
    setUpdForm(EMPTY_UPDATE);
    setUpdates([]);
    loadUpdates(c.id);
    setUpdOpen(true);
  };

  const addUpdate = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/admin/campaigns/${updCampaign.id}/updates`, updForm);
      toast.success("Update published");
      setUpdForm(EMPTY_UPDATE);
      loadUpdates(updCampaign.id);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error");
    }
  };

  const deleteUpdate = async (uid) => {
    try {
      await api.delete(`/admin/updates/${uid}`);
      toast.success("Update deleted");
      loadUpdates(updCampaign.id);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error");
    }
  };

  const downloadBlob = (data, filename) => {
    const url = URL.createObjectURL(new Blob([data], { type: "application/pdf" }));
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  };

  const downloadReceipt = async (d) => {
    try {
      const r = await api.get(`/admin/donations/${d.id}/receipt`, { responseType: "blob" });
      downloadBlob(r.data, `donation-receipt-${d.receipt_no || d.id}.pdf`);
    } catch {
      toast.error("Receipt unavailable");
    }
  };

  const toggleAnonymous = async (d) => {
    try {
      const r = await api.post(`/admin/donations/${d.id}/anonymous`);
      toast.success(r.data.anonymous ? "Donor hidden from supporters wall" : "Donor name visible again");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error");
    }
  };

  const addMember = async (e) => {
    e.preventDefault();
    try {
      await api.post("/admin/members", { ...memberForm, amount: Number(memberForm.amount) || 0 });
      toast.success("Member added");
      setMemberOpen(false);
      setMemberForm({ name: "", email: "", plan: "yearly", amount: 100, start_date: new Date().toISOString().slice(0, 10) });
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error");
    }
  };

  const deactivateMember = async (id) => {
    try {
      await api.post(`/admin/members/${id}/deactivate`);
      toast.success("Member deactivated");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error");
    }
  };

  const loadCerts = async () => {
    try {
      const r = await api.get(`/admin/certificates/${certYear}`);
      setCertRows(r.data);
    } catch {
      toast.error("Could not load donors");
    }
  };

  const downloadCert = async (email) => {
    try {
      const r = await api.get(`/admin/certificates/${certYear}/pdf`, { params: { email }, responseType: "blob" });
      downloadBlob(r.data, `tax-certificate-${certYear}-${email}.pdf`);
    } catch {
      toast.error("Certificate unavailable");
    }
  };

  const sendCert = async (email) => {
    try {
      await api.post(`/admin/certificates/${certYear}/send`, null, { params: { email } });
      toast.success(`Certificate emailed to ${email}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error");
    }
  };

  const uploadPhoto = async (file, apply) => {
    const fd = new FormData();
    fd.append("file", file);
    setUploading(true);
    try {
      const r = await api.post("/admin/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
      apply(r.data.url);
      toast.success("Photo uploaded");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const loadNewsletter = useCallback(async () => {
    try {
      const [s, h] = await Promise.all([api.get("/admin/newsletter/subscribers"), api.get("/admin/newsletter/history")]);
      setNlSubs(s.data);
      setNlHistory(h.data);
    } catch {
      toast.error("Could not load newsletter data");
    }
  }, []);

  useEffect(() => {
    if (me && tab === "newsletter") loadNewsletter();
  }, [me, tab, loadNewsletter]);

  useEffect(() => {
    if (me && tab === "visitors") {
      api.get("/admin/visitors").then((r) => setVisitors(r.data)).catch(() => toast.error("Could not load visitors"));
    }
  }, [me, tab]);

  const sendNewsletter = async (e) => {
    e.preventDefault();
    if (!window.confirm(`Send this newsletter to ${nlSubs?.count || 0} subscribers?`)) return;
    setNlSending(true);
    try {
      const r = await api.post("/admin/newsletter/send", nlData);
      toast.success(`Newsletter sent to ${r.data.recipients} subscribers`);
      setNlData({ subject_en: "", subject_fr: "", body_en: "", body_fr: "" });
      loadNewsletter();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error");
    } finally {
      setNlSending(false);
    }
  };

  if (!me) return <div className="min-h-screen bg-[#FAFAFA] flex items-center justify-center text-forest" data-testid="admin-loading">Loading…</div>;

  const tabs = [
    { id: "overview", label: "Overview", icon: LayoutDashboard },
    { id: "campaigns", label: "Campaigns", icon: Megaphone },
    { id: "donations", label: "Donations", icon: HandCoins },
    { id: "members", label: "Members", icon: Users },
    { id: "certificates", label: "Tax receipts", icon: FileText },
    { id: "newsletter", label: "Newsletter", icon: Send },
    { id: "visitors", label: "Visitors", icon: Globe },
    { id: "messages", label: "Messages", icon: Mail },
  ];

  const statusBadge = (s) => {
    const styles = {
      paid: "bg-moss/10 text-moss border-moss/30",
      confirmed: "bg-moss/10 text-moss border-moss/30",
      active: "bg-moss/10 text-moss border-moss/30",
      pending: "bg-amber-100 text-amber-800 border-amber-300",
      past: "bg-forest/10 text-forest border-forest/20",
      failed: "bg-red-100 text-red-700 border-red-300",
      to_be_renewed: "bg-orange-100 text-orange-800 border-orange-300",
      inactive: "bg-forest/10 text-forest/60 border-forest/20",
    };
    return <Badge variant="outline" className={styles[s] || "bg-forest/5 text-forest"}>{(s || "").replace(/_/g, " ")}</Badge>;
  };

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-[#FAFAFA] flex">
      <aside className="w-60 bg-forest text-cream flex flex-col shrink-0 min-h-screen sticky top-0">
        <div className="p-6 border-b border-cream/10">
          <p className="font-serif text-2xl font-semibold">Future Gen</p>
          <p className="text-[10px] uppercase tracking-[0.25em] text-cream/50 mt-1">ASBL · Admin</p>
        </div>
        <nav className="flex-1 p-4 flex flex-col gap-1">
          {tabs.map((tb) => (
            <button
              key={tb.id}
              data-testid={`admin-tab-${tb.id}`}
              onClick={() => setTab(tb.id)}
              className={`flex items-center gap-3 px-4 py-3 text-sm font-medium text-left transition-colors duration-300 ${tab === tb.id ? "bg-cream/10 text-cream" : "text-cream/60 hover:text-cream hover:bg-cream/5"}`}
            >
              <tb.icon size={17} /> {tb.label}
            </button>
          ))}
        </nav>
        <div className="p-4 border-t border-cream/10">
          <p className="text-xs text-cream/50 px-4 mb-2 truncate" data-testid="admin-user-email">{me.email}</p>
          <button data-testid="admin-logout-btn" onClick={logout} className="flex items-center gap-3 px-4 py-3 text-sm text-cream/60 hover:text-cream transition-colors duration-300 w-full">
            <LogOut size={17} /> Sign out
          </button>
        </div>
      </aside>

      <main className="flex-1 p-8 md:p-12 overflow-x-hidden">
        {tab === "overview" && stats && (
          <div data-testid="admin-overview">
            <h1 className="font-serif text-4xl text-forest mb-10">Overview</h1>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              {[
                { label: "Total raised", value: fmt(stats.total_raised), id: "admin-stat-raised" },
                { label: "Active campaigns", value: `${stats.active_campaigns} / ${stats.campaigns}`, id: "admin-stat-campaigns" },
                { label: "Members (active / pending)", value: `${stats.members_active} / ${stats.members_pending}`, id: "admin-stat-members" },
                { label: "Donations (pending pledges)", value: `${stats.donations} (${stats.pending_pledges})`, id: "admin-stat-donations" },
                { label: "Contact messages", value: stats.messages, id: "admin-stat-messages" },
              ].map((s) => (
                <div key={s.id} className="bg-white border border-[#E5E5E5] p-6">
                  <p data-testid={s.id} className="font-serif text-3xl font-semibold text-forest">{s.value}</p>
                  <p className="mt-2 text-xs text-forest/50 uppercase tracking-wider">{s.label}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {tab === "campaigns" && (
          <div data-testid="admin-campaigns">
            <div className="flex items-center justify-between mb-10">
              <h1 className="font-serif text-4xl text-forest">Campaigns</h1>
              <Button data-testid="campaign-create-btn" onClick={openCreate} className="bg-forest text-cream hover:bg-clay rounded-full gap-2">
                <Plus size={16} /> New campaign
              </Button>
            </div>
            <div className="bg-white border border-[#E5E5E5]">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Title</TableHead>
                    <TableHead>Category</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Raised / Goal</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {campaigns.map((c) => (
                    <TableRow key={c.id} data-testid={`admin-campaign-row-${c.id}`}>
                      <TableCell className="font-medium text-forest">{c.title_en}</TableCell>
                      <TableCell className="capitalize">{c.category}</TableCell>
                      <TableCell>{statusBadge(c.status)}</TableCell>
                      <TableCell>{fmt(c.raised_amount)} / {fmt(c.goal_amount)}</TableCell>
                      <TableCell className="text-right">
                        <button data-testid={`campaign-updates-${c.id}`} title="Manage updates" onClick={() => openUpdates(c)} className="p-2 text-forest/60 hover:text-moss transition-colors duration-300"><Newspaper size={16} /></button>
                        <button data-testid={`campaign-edit-${c.id}`} onClick={() => openEdit(c)} className="p-2 text-forest/60 hover:text-clay transition-colors duration-300"><Pencil size={16} /></button>
                        <button data-testid={`campaign-delete-${c.id}`} onClick={() => deleteCampaign(c.id)} className="p-2 text-forest/60 hover:text-red-600 transition-colors duration-300"><Trash2 size={16} /></button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>
        )}

        {tab === "donations" && (
          <div data-testid="admin-donations">
            <h1 className="font-serif text-4xl text-forest mb-10">Donations</h1>
            <div className="bg-white border border-[#E5E5E5]">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Date</TableHead>
                    <TableHead>Donor</TableHead>
                    <TableHead>Campaign</TableHead>
                    <TableHead>Amount</TableHead>
                    <TableHead>Method</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Action</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {donations.length === 0 && <TableRow><TableCell colSpan={7} className="text-center text-forest/40 py-10">No donations yet</TableCell></TableRow>}
                  {donations.map((d) => (
                    <TableRow key={d.id} data-testid={`admin-donation-row-${d.id}`}>
                      <TableCell>{fmtDate(d.created_at)}</TableCell>
                      <TableCell>
                        <p className="font-medium text-forest">{d.name} {d.anonymous && <span className="text-xs font-normal italic text-orange-700">(anonymous)</span>}</p>
                        <p className="text-xs text-forest/50">{d.email}</p>
                      </TableCell>
                      <TableCell className="max-w-[180px] truncate">{d.campaign_title || "—"}</TableCell>
                      <TableCell className="font-medium">{fmt(d.amount)}</TableCell>
                      <TableCell className="capitalize">{d.method}</TableCell>
                      <TableCell>{statusBadge(d.status)}</TableCell>
                      <TableCell className="text-right">
                        <button
                          data-testid={`donation-anonymous-${d.id}`}
                          title={d.anonymous ? "Show donor name on supporters wall" : "Hide donor from supporters wall (anonymise)"}
                          onClick={() => toggleAnonymous(d)}
                          className={`p-2 transition-colors duration-300 ${d.anonymous ? "text-orange-600 hover:text-forest" : "text-forest/60 hover:text-orange-600"}`}
                        >
                          {d.anonymous ? <EyeOff size={16} /> : <Eye size={16} />}
                        </button>
                        {(d.status === "paid" || d.status === "confirmed") && (
                          <button data-testid={`donation-receipt-${d.id}`} title="Download receipt" onClick={() => downloadReceipt(d)} className="p-2 text-forest/60 hover:text-clay transition-colors duration-300"><Download size={16} /></button>
                        )}
                        {d.status === "pending" && (
                          <Button data-testid={`donation-confirm-${d.id}`} size="sm" onClick={() => confirmDonation(d.id)} className="bg-moss text-white hover:bg-forest rounded-full gap-1">
                            <Check size={14} /> Confirm
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>
        )}

        {tab === "members" && (() => {
          const sortMembers = (key) => setMemberSort((s) => ({ key, dir: s.key === key && s.dir === "asc" ? "desc" : "asc" }));
          const sortedMembers = [...members].sort((a, b) => {
            const va = (a[memberSort.key] ?? "").toString().toLowerCase();
            const vb = (b[memberSort.key] ?? "").toString().toLowerCase();
            return memberSort.dir === "asc" ? va.localeCompare(vb) : vb.localeCompare(va);
          });
          const sortHead = (k, label) => (
            <TableHead>
              <button data-testid={`member-sort-${k}`} onClick={() => sortMembers(k)} className="inline-flex items-center gap-1 font-medium hover:text-clay transition-colors duration-300">
                {label} <ArrowUpDown size={12} className={memberSort.key === k ? "text-clay" : "text-forest/30"} />
              </button>
            </TableHead>
          );
          return (
          <div data-testid="admin-members">
            <div className="flex items-center justify-between mb-4">
              <h1 className="font-serif text-4xl text-forest">Members</h1>
              <Button data-testid="member-add-btn" onClick={() => setMemberOpen(true)} className="bg-forest text-cream hover:bg-clay rounded-full gap-1"><UserPlus size={15} /> Add member</Button>
            </div>
            <p className="text-sm text-forest/60 mb-8 max-w-xl">Membership runs for one year. Members active for more than a year are flagged <span className="font-semibold text-orange-700">to be renewed</span> — chase them, then click Mark renewed once their payment is in.</p>
            <div className="bg-white border border-[#E5E5E5]">
              <Table>
                <TableHeader>
                  <TableRow>
                    {sortHead("created_at", "Since")}
                    {sortHead("name", "Member")}
                    {sortHead("plan", "Plan")}
                    {sortHead("method", "Method")}
                    {sortHead("status", "Status")}
                    <TableHead className="text-right">Action</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {members.length === 0 && <TableRow><TableCell colSpan={6} className="text-center text-forest/40 py-10">No members yet</TableCell></TableRow>}
                  {sortedMembers.map((m) => (
                    <TableRow key={m.id} data-testid={`admin-member-row-${m.id}`}>
                      <TableCell>
                        {fmtDate(m.created_at)}
                        {m.activated_at && m.activated_at.slice(0, 10) !== (m.created_at || "").slice(0, 10) && (
                          <p className="text-xs text-forest/50">renewed {fmtDate(m.activated_at)}</p>
                        )}
                      </TableCell>
                      <TableCell>
                        <p className="font-medium text-forest">{m.name}</p>
                        <p className="text-xs text-forest/50">{m.email}</p>
                      </TableCell>
                      <TableCell className="capitalize">{m.plan}{m.amount_paid ? ` · €${m.amount_paid}` : ""}</TableCell>
                      <TableCell className="capitalize">{m.method}</TableCell>
                      <TableCell>{statusBadge(m.status)}</TableCell>
                      <TableCell className="text-right">
                        {m.status === "pending" && (
                          <Button data-testid={`member-activate-${m.id}`} size="sm" onClick={() => activateMember(m.id)} className="bg-moss text-white hover:bg-forest rounded-full gap-1">
                            <Check size={14} /> Activate
                          </Button>
                        )}
                        {m.status === "to_be_renewed" && (
                          <Button data-testid={`member-renew-${m.id}`} size="sm" onClick={() => activateMember(m.id)} className="bg-clay text-cream hover:bg-forest rounded-full gap-1">
                            <RefreshCw size={14} /> Mark renewed
                          </Button>
                        )}
                        {m.status === "inactive" && (
                          <Button data-testid={`member-reactivate-${m.id}`} size="sm" onClick={() => activateMember(m.id)} className="bg-moss text-white hover:bg-forest rounded-full gap-1">
                            <Check size={14} /> Reactivate
                          </Button>
                        )}
                        {(m.status === "active" || m.status === "to_be_renewed") && (
                          <button data-testid={`member-deactivate-${m.id}`} title="Deactivate member" onClick={() => deactivateMember(m.id)} className="p-2 ml-1 text-forest/50 hover:text-red-600 transition-colors duration-300">
                            <Ban size={16} />
                          </button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>
          );
        })()}

        {tab === "certificates" && (
          <div data-testid="admin-certificates">
            <h1 className="font-serif text-4xl text-forest mb-4">Tax receipts</h1>
            <p className="text-sm text-forest/60 mb-8 max-w-xl">Annual donation certificates per donor (paid & confirmed donations). Download the PDF or email it directly to the donor from contact@future-gen.eu. Individual receipts can be downloaded from the Donations tab.</p>
            <div className="flex items-center gap-3 mb-8">
              <Input data-testid="cert-year-input" type="number" className="w-32 bg-white" value={certYear} onChange={(e) => setCertYear(e.target.value)} />
              <Button data-testid="cert-load-btn" onClick={loadCerts} className="bg-forest text-cream hover:bg-clay rounded-full">Load donors</Button>
            </div>
            {certRows && (
              <div className="bg-white border border-[#E5E5E5]">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Donor</TableHead>
                      <TableHead>Donations</TableHead>
                      <TableHead>Total</TableHead>
                      <TableHead className="text-right">Certificate</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {certRows.length === 0 && <TableRow><TableCell colSpan={4} className="text-center text-forest/40 py-10">No paid or confirmed donations in {certYear}</TableCell></TableRow>}
                    {certRows.map((r) => (
                      <TableRow key={r.email} data-testid={`cert-row-${r.email}`}>
                        <TableCell>
                          <p className="font-medium text-forest">{r.name}</p>
                          <p className="text-xs text-forest/50">{r.email}</p>
                        </TableCell>
                        <TableCell>{r.count}</TableCell>
                        <TableCell className="font-medium">{fmt(r.total)}</TableCell>
                        <TableCell className="text-right">
                          <Button data-testid={`cert-download-${r.email}`} size="sm" variant="outline" onClick={() => downloadCert(r.email)} className="rounded-full gap-1 mr-2"><Download size={14} /> PDF</Button>
                          <Button data-testid={`cert-send-${r.email}`} size="sm" onClick={() => sendCert(r.email)} className="bg-forest text-cream hover:bg-clay rounded-full gap-1"><Send size={14} /> Email donor</Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </div>
        )}

        {tab === "newsletter" && (
          <div data-testid="admin-newsletter">
            <h1 className="font-serif text-4xl text-forest mb-4">Newsletter</h1>
            <p className="text-sm text-forest/60 mb-8 max-w-xl">
              {nlSubs ? `${nlSubs.count} active subscriber${nlSubs.count === 1 ? "" : "s"}.` : "Loading subscribers…"} Each subscriber receives the newsletter in their chosen language with an unsubscribe link.
            </p>
            <form onSubmit={sendNewsletter} className="bg-white border border-[#E5E5E5] p-6 flex flex-col gap-4 max-w-3xl mb-10" data-testid="newsletter-compose-form">
              <div className="grid grid-cols-2 gap-4">
                <Input data-testid="nl-subject-en" required placeholder="Subject (EN)" value={nlData.subject_en} onChange={(e) => setNlData({ ...nlData, subject_en: e.target.value })} />
                <Input data-testid="nl-subject-fr" required placeholder="Sujet (FR)" value={nlData.subject_fr} onChange={(e) => setNlData({ ...nlData, subject_fr: e.target.value })} />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <Textarea data-testid="nl-body-en" required placeholder="Message (EN)" rows={6} value={nlData.body_en} onChange={(e) => setNlData({ ...nlData, body_en: e.target.value })} />
                <Textarea data-testid="nl-body-fr" required placeholder="Message (FR)" rows={6} value={nlData.body_fr} onChange={(e) => setNlData({ ...nlData, body_fr: e.target.value })} />
              </div>
              <div className="flex justify-end">
                <Button data-testid="nl-send-btn" type="submit" disabled={nlSending || !nlSubs?.count} className="bg-forest text-cream hover:bg-clay rounded-full gap-1">
                  <Send size={14} /> {nlSending ? "Sending…" : `Send to ${nlSubs?.count || 0} subscribers`}
                </Button>
              </div>
            </form>
            {nlHistory.length > 0 && (
              <div className="mb-10 max-w-3xl">
                <p className="text-xs uppercase tracking-[0.2em] text-forest/50 mb-3">Past sends</p>
                <div className="flex flex-col gap-2">
                  {nlHistory.map((h) => (
                    <div key={h.id} className="bg-white border border-[#E5E5E5] px-4 py-3 flex justify-between items-center text-sm" data-testid={`nl-history-${h.id}`}>
                      <span className="text-forest font-medium">{h.subject_en}</span>
                      <span className="text-forest/50">{fmtDate(h.created_at)} · {h.sent_to} recipients</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {nlSubs && (
              <div className="bg-white border border-[#E5E5E5] max-w-3xl">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Email</TableHead>
                      <TableHead>Language</TableHead>
                      <TableHead>Subscribed</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {nlSubs.subscribers.length === 0 && <TableRow><TableCell colSpan={3} className="text-center text-forest/40 py-10">No subscribers yet</TableCell></TableRow>}
                    {nlSubs.subscribers.map((s) => (
                      <TableRow key={s.id} data-testid={`nl-sub-${s.email}`}>
                        <TableCell className="font-medium text-forest">{s.email}</TableCell>
                        <TableCell className="uppercase">{s.lang}</TableCell>
                        <TableCell>{fmtDate(s.created_at)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </div>
        )}

        {tab === "visitors" && (
          <div data-testid="admin-visitors">
            <h1 className="font-serif text-4xl text-forest mb-4">Visitors</h1>
            <p className="text-sm text-forest/60 mb-8 max-w-xl">
              {visitors ? `${visitors.total} home page visit${visitors.total === 1 ? "" : "s"} logged (showing last ${visitors.visits.length}).` : "Loading…"} Repeat visits from the same IP within 30 minutes are counted once. Times shown in Europe/Luxembourg.
            </p>
            {visitors && (
              <div className="bg-white border border-[#E5E5E5]">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Date & time</TableHead>
                      <TableHead>IP address</TableHead>
                      <TableHead>City</TableHead>
                      <TableHead>Country</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {visitors.visits.length === 0 && <TableRow><TableCell colSpan={4} className="text-center text-forest/40 py-10">No visits logged yet</TableCell></TableRow>}
                    {visitors.visits.map((v) => (
                      <TableRow key={v.id} data-testid={`visitor-row-${v.id}`}>
                        <TableCell>{new Date(v.created_at).toLocaleString("en-GB", { timeZone: "Europe/Luxembourg", day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })}</TableCell>
                        <TableCell className="font-mono text-xs">{v.ip}</TableCell>
                        <TableCell>{v.city || "—"}</TableCell>
                        <TableCell>{v.country ? `${v.country}${v.country_code ? ` (${v.country_code})` : ""}` : "—"}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </div>
        )}

        {tab === "messages" && (
          <div data-testid="admin-messages">
            <h1 className="font-serif text-4xl text-forest mb-10">Messages</h1>
            {suggestions.length > 0 && (
              <div className="mb-12" data-testid="admin-suggestions">
                <p className="text-xs uppercase tracking-[0.2em] text-forest/50 mb-4">Campaign suggestions</p>
                <div className="flex flex-col gap-4">
                  {suggestions.map((s) => (
                    <div key={s.id} data-testid={`admin-suggestion-${s.id}`} className="bg-white border-l-4 border-clay border border-[#E5E5E5] p-6">
                      <div className="flex flex-wrap items-baseline justify-between gap-2 mb-3">
                        <p className="font-medium text-forest">{s.title}</p>
                        <p className="text-xs text-forest/40">{fmtDate(s.created_at)}</p>
                      </div>
                      <p className="text-sm text-forest/70 leading-relaxed whitespace-pre-wrap mb-3">{s.description}</p>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs text-forest/60">
                        <p><span className="font-semibold text-forest">Objective:</span> {s.objective}</p>
                        <p><span className="font-semibold text-forest">Fund source:</span> {s.fund_source}</p>
                        <p><span className="font-semibold text-forest">Distribution:</span> {s.fund_distribution}</p>
                        <p><span className="font-semibold text-forest">Contact:</span> {s.contact_name} · {s.contact_email} · {s.contact_phone}</p>
                      </div>
                    </div>
                  ))}
                </div>
                <p className="text-xs uppercase tracking-[0.2em] text-forest/50 mt-12 mb-4">Contact messages</p>
              </div>
            )}
            <div className="flex flex-col gap-4">
              {contacts.length === 0 && <p className="text-forest/40">No messages yet</p>}
              {contacts.map((c) => (
                <div key={c.id} data-testid={`admin-message-${c.id}`} className="bg-white border border-[#E5E5E5] p-6">
                  <div className="flex flex-wrap items-baseline justify-between gap-2 mb-3">
                    <p className="font-medium text-forest">{c.name} <span className="text-forest/50 font-normal text-sm">· {c.email}</span></p>
                    <p className="text-xs text-forest/40">{fmtDate(c.created_at)}</p>
                  </div>
                  {c.subject && <p className="text-sm font-medium text-forest mb-1">{c.subject}</p>}
                  <p className="text-sm text-forest/70 leading-relaxed whitespace-pre-wrap">{c.message}</p>
                </div>
              ))}
            </div>
          </div>
        )}
      </main>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto bg-white">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl text-forest">{editing ? "Edit campaign" : "New campaign"}</DialogTitle>
            <DialogDescription className="sr-only">Campaign editor form</DialogDescription>
          </DialogHeader>
          <form onSubmit={saveCampaign} className="flex flex-col gap-4 mt-4" data-testid="campaign-form">
            <div className="grid grid-cols-2 gap-4">
              <Input data-testid="campaign-title-en" required placeholder="Title (EN)" value={form.title_en} onChange={(e) => setForm({ ...form, title_en: e.target.value })} />
              <Input data-testid="campaign-title-fr" required placeholder="Titre (FR)" value={form.title_fr} onChange={(e) => setForm({ ...form, title_fr: e.target.value })} />
              <Input data-testid="campaign-title-de" placeholder="Titel (DE) — optional" value={form.title_de || ""} onChange={(e) => setForm({ ...form, title_de: e.target.value })} />
              <Input data-testid="campaign-title-tr" placeholder="Başlık (TR) — optional" value={form.title_tr || ""} onChange={(e) => setForm({ ...form, title_tr: e.target.value })} />
            </div>
            <div className="grid grid-cols-3 gap-4">
              <select data-testid="campaign-category" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="border border-input bg-white px-3 py-2 text-sm">
                <option value="youth">Youth</option>
                <option value="education">Education</option>
                <option value="poverty">Poverty</option>
              </select>
              <select data-testid="campaign-status" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} className="border border-input bg-white px-3 py-2 text-sm">
                <option value="active">Active</option>
                <option value="past">Past</option>
              </select>
              <Input data-testid="campaign-goal" type="number" min="0" placeholder="Goal (EUR)" value={form.goal_amount} onChange={(e) => setForm({ ...form, goal_amount: e.target.value })} />
              <Input data-testid="campaign-raised" type="number" min="0" step="0.01" placeholder="Raised (EUR)" value={form.raised_amount} onChange={(e) => setForm({ ...form, raised_amount: e.target.value })} />
            </div>
            <div className="flex gap-2">
              <Input data-testid="campaign-image-url" placeholder="Image URL" value={form.image_url} onChange={(e) => setForm({ ...form, image_url: e.target.value })} />
              <input ref={campaignFileRef} type="file" accept="image/jpeg,image/png,image/webp,image/gif" className="hidden" data-testid="campaign-image-file" onChange={(e) => { const f = e.target.files[0]; if (f) uploadPhoto(f, (url) => setForm((prev) => ({ ...prev, image_url: url }))); e.target.value = ""; }} />
              <Button type="button" data-testid="campaign-upload-btn" variant="outline" disabled={uploading} onClick={() => campaignFileRef.current.click()} className="rounded-full gap-1 shrink-0"><Upload size={14} /> {uploading ? "Uploading…" : "Upload"}</Button>
            </div>
            {form.image_url && <img src={form.image_url} alt="Campaign preview" className="h-20 w-32 object-cover border border-[#E5E5E5]" data-testid="campaign-image-preview" />}
            <div>
              <Input data-testid="campaign-slug" placeholder="URL alias (e.g. hiver-chaud — auto if empty)" value={form.slug || ""} onChange={(e) => setForm({ ...form, slug: e.target.value })} />
              <p className="text-xs text-forest/40 mt-1 pl-1">Public link: /campaigns/{form.slug || "…"}</p>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <Textarea data-testid="campaign-summary-en" placeholder="Summary (EN)" rows={2} value={form.summary_en} onChange={(e) => setForm({ ...form, summary_en: e.target.value })} />
              <Textarea data-testid="campaign-summary-fr" placeholder="Résumé (FR)" rows={2} value={form.summary_fr} onChange={(e) => setForm({ ...form, summary_fr: e.target.value })} />
              <Textarea data-testid="campaign-summary-de" placeholder="Zusammenfassung (DE) — optional" rows={2} value={form.summary_de || ""} onChange={(e) => setForm({ ...form, summary_de: e.target.value })} />
              <Textarea data-testid="campaign-summary-tr" placeholder="Özet (TR) — optional" rows={2} value={form.summary_tr || ""} onChange={(e) => setForm({ ...form, summary_tr: e.target.value })} />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <Textarea data-testid="campaign-description-en" placeholder="Description (EN)" rows={4} value={form.description_en} onChange={(e) => setForm({ ...form, description_en: e.target.value })} />
              <Textarea data-testid="campaign-description-fr" placeholder="Description (FR)" rows={4} value={form.description_fr} onChange={(e) => setForm({ ...form, description_fr: e.target.value })} />
              <Textarea data-testid="campaign-description-de" placeholder="Beschreibung (DE) — optional" rows={4} value={form.description_de || ""} onChange={(e) => setForm({ ...form, description_de: e.target.value })} />
              <Textarea data-testid="campaign-description-tr" placeholder="Açıklama (TR) — optional" rows={4} value={form.description_tr || ""} onChange={(e) => setForm({ ...form, description_tr: e.target.value })} />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <Textarea data-testid="campaign-purpose-en" placeholder="Purpose items (EN) — one per line" rows={3} value={form.purpose_en} onChange={(e) => setForm({ ...form, purpose_en: e.target.value })} />
              <Textarea data-testid="campaign-purpose-fr" placeholder="Objectifs (FR) — un par ligne" rows={3} value={form.purpose_fr} onChange={(e) => setForm({ ...form, purpose_fr: e.target.value })} />
              <Textarea data-testid="campaign-purpose-de" placeholder="Ziele (DE) — eins pro Zeile, optional" rows={3} value={form.purpose_de || ""} onChange={(e) => setForm({ ...form, purpose_de: e.target.value })} />
              <Textarea data-testid="campaign-purpose-tr" placeholder="Hedefler (TR) — her satıra bir, optional" rows={3} value={form.purpose_tr || ""} onChange={(e) => setForm({ ...form, purpose_tr: e.target.value })} />
            </div>
            <div className="flex justify-end gap-3 mt-2">
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} className="rounded-full gap-1"><X size={14} /> Cancel</Button>
              <Button data-testid="campaign-save-btn" type="submit" className="bg-forest text-cream hover:bg-clay rounded-full gap-1"><Check size={14} /> Save</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={updOpen} onOpenChange={setUpdOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto bg-white">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl text-forest">Updates — {updCampaign?.title_en}</DialogTitle>
            <DialogDescription className="sr-only">Publish and manage campaign updates</DialogDescription>
          </DialogHeader>
          <form onSubmit={addUpdate} className="flex flex-col gap-4 mt-2 border-b border-[#E5E5E5] pb-6" data-testid="update-form">
            <div className="grid grid-cols-2 gap-4">
              <Input data-testid="update-title-en" required placeholder="Update title (EN)" value={updForm.title_en} onChange={(e) => setUpdForm({ ...updForm, title_en: e.target.value })} />
              <Input data-testid="update-title-fr" required placeholder="Titre (FR)" value={updForm.title_fr} onChange={(e) => setUpdForm({ ...updForm, title_fr: e.target.value })} />
              <Input data-testid="update-title-de" placeholder="Titel (DE) — optional" value={updForm.title_de || ""} onChange={(e) => setUpdForm({ ...updForm, title_de: e.target.value })} />
              <Input data-testid="update-title-tr" placeholder="Başlık (TR) — optional" value={updForm.title_tr || ""} onChange={(e) => setUpdForm({ ...updForm, title_tr: e.target.value })} />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <Textarea data-testid="update-body-en" placeholder="Update text (EN)" rows={3} value={updForm.body_en} onChange={(e) => setUpdForm({ ...updForm, body_en: e.target.value })} />
              <Textarea data-testid="update-body-fr" placeholder="Texte (FR)" rows={3} value={updForm.body_fr} onChange={(e) => setUpdForm({ ...updForm, body_fr: e.target.value })} />
              <Textarea data-testid="update-body-de" placeholder="Text (DE) — optional" rows={3} value={updForm.body_de || ""} onChange={(e) => setUpdForm({ ...updForm, body_de: e.target.value })} />
              <Textarea data-testid="update-body-tr" placeholder="Metin (TR) — optional" rows={3} value={updForm.body_tr || ""} onChange={(e) => setUpdForm({ ...updForm, body_tr: e.target.value })} />
            </div>
            <div className="flex gap-2">
              <Input data-testid="update-image-url" placeholder="Photo URL (optional)" value={updForm.image_url} onChange={(e) => setUpdForm({ ...updForm, image_url: e.target.value })} />
              <input ref={updateFileRef} type="file" accept="image/jpeg,image/png,image/webp,image/gif" className="hidden" data-testid="update-image-file" onChange={(e) => { const f = e.target.files[0]; if (f) uploadPhoto(f, (url) => setUpdForm((prev) => ({ ...prev, image_url: url }))); e.target.value = ""; }} />
              <Button type="button" data-testid="update-upload-btn" variant="outline" disabled={uploading} onClick={() => updateFileRef.current.click()} className="rounded-full gap-1 shrink-0"><Upload size={14} /> {uploading ? "Uploading…" : "Upload"}</Button>
            </div>
            {updForm.image_url && <img src={updForm.image_url} alt="Update preview" className="h-20 w-32 object-cover border border-[#E5E5E5]" data-testid="update-image-preview" />}
            <div className="flex justify-end">
              <Button data-testid="update-save-btn" type="submit" className="bg-forest text-cream hover:bg-clay rounded-full gap-1"><Plus size={14} /> Publish update</Button>
            </div>
          </form>
          <div className="flex flex-col gap-3 mt-2" data-testid="updates-list">
            {updates.length === 0 && <p className="text-sm text-forest/40">No updates yet</p>}
            {updates.map((u) => (
              <div key={u.id} data-testid={`update-item-${u.id}`} className="border border-[#E5E5E5] p-4 flex items-start justify-between gap-3">
                <div>
                  <p className="text-xs text-forest/40">{fmtDate(u.created_at)}</p>
                  <p className="font-medium text-forest mt-0.5">{u.title_en}</p>
                  <p className="text-sm text-forest/60 mt-1 line-clamp-2">{u.body_en}</p>
                </div>
                <button data-testid={`update-delete-${u.id}`} onClick={() => deleteUpdate(u.id)} className="p-2 text-forest/60 hover:text-red-600 transition-colors duration-300 shrink-0"><Trash2 size={16} /></button>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
      <Dialog open={memberOpen} onOpenChange={setMemberOpen}>
        <DialogContent className="max-w-md bg-white">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl text-forest">Add member manually</DialogTitle>
            <DialogDescription className="sr-only">Manual member creation form</DialogDescription>
          </DialogHeader>
          <form onSubmit={addMember} className="flex flex-col gap-4 mt-2" data-testid="member-form">
            <Input data-testid="member-name-input" required placeholder="Full name" value={memberForm.name} onChange={(e) => setMemberForm({ ...memberForm, name: e.target.value })} />
            <Input data-testid="member-email-input" required type="email" placeholder="Email" value={memberForm.email} onChange={(e) => setMemberForm({ ...memberForm, email: e.target.value })} />
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs text-forest/50 mb-1 pl-1">Plan</label>
                <select data-testid="member-plan-select" value={memberForm.plan} onChange={(e) => setMemberForm({ ...memberForm, plan: e.target.value, amount: e.target.value === "monthly" ? 10 : 100 })} className="w-full border border-input rounded-md px-3 py-2 text-sm bg-white">
                  <option value="monthly">Monthly (€10)</option>
                  <option value="yearly">Yearly (€100)</option>
                </select>
              </div>
              <div>
                <label className="block text-xs text-forest/50 mb-1 pl-1">Amount paid (€)</label>
                <Input data-testid="member-amount-input" type="number" min="0" step="0.01" value={memberForm.amount} onChange={(e) => setMemberForm({ ...memberForm, amount: e.target.value })} />
              </div>
            </div>
            <div>
              <label className="block text-xs text-forest/50 mb-1 pl-1">Start date</label>
              <Input data-testid="member-start-date-input" type="date" required value={memberForm.start_date} onChange={(e) => setMemberForm({ ...memberForm, start_date: e.target.value })} />
            </div>
            <div className="flex justify-end">
              <Button data-testid="member-save-btn" type="submit" className="bg-forest text-cream hover:bg-clay rounded-full gap-1"><UserPlus size={14} /> Add member</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Admin;
